import { jsPDF } from 'jspdf';
import { formatPKR } from './currencyFormatter';
import { addPdfBrandLogo } from './pdfLogo';

const safeText = (value) => {
  if (value == null) return '—';
  const text = String(value).trim();
  return text || '—';
};

const isInflow = (type) => type === 'credit' || type === 'transfer_in';

const formatDateValue = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-PK');
};

const formatTimeValue = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
};

const calcCurrentBalance = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  for (const row of rows) {
    const bal = parseFloat(row.balance_after ?? row.balanceAfter);
    if (Number.isFinite(bal)) return bal;
  }
  return 0;
};

export async function downloadAccountStatementPdf({
  businessName,
  branchName,
  accountFilterLabel,
  fromDate,
  toDate,
  rows,
  generatedAt = new Date()
}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const lineH = 5;

  const { nextY } = await addPdfBrandLogo(doc, { maxWidthMm: 34, startY: 8, gapAfterMm: 3 });
  let y = nextY;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Cash / Bank Statement', margin, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text(`Business: ${safeText(businessName)}`, margin, y);
  y += lineH;
  doc.text(`Branch: ${safeText(branchName)}`, margin, y);
  y += lineH;
  doc.text(`Account: ${safeText(accountFilterLabel || 'All accounts')}`, margin, y);
  y += lineH;
  doc.text(`Period: ${safeText(fromDate)} to ${safeText(toDate)}`, margin, y);
  y += lineH;
  doc.text(`Generated: ${generatedAt.toLocaleString('en-PK')}`, margin, y);
  y += lineH;
  doc.setFont('helvetica', 'bold');
  doc.text(`Current Balance: ${formatPKR(calcCurrentBalance(rows))}`, margin, y);
  y += 7;

  const cols = {
    date: margin,
    time: margin + 21,
    account: margin + 45,
    in: margin + 81,
    out: margin + 103,
    balance: margin + 125,
    counterparty: margin + 148,
    desc: margin + 174
  };

  const drawHeader = () => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(31, 41, 55);
    doc.text('Date', cols.date, y);
    doc.text('Time', cols.time, y);
    doc.text('Account', cols.account, y);
    doc.text('In', cols.in, y, { align: 'right' });
    doc.text('Out', cols.out, y, { align: 'right' });
    doc.text('Balance', cols.balance, y, { align: 'right' });
    doc.text('From / To', cols.counterparty, y);
    doc.text('Description / Ref', cols.desc, y);
    y += 2.5;
    doc.setDrawColor(203, 213, 225);
    doc.line(margin, y, pageW - margin, y);
    y += 4.5;
  };

  drawHeader();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.7);
  doc.setTextColor(30, 41, 59);

  const data = Array.isArray(rows) ? rows : [];
  if (data.length === 0) {
    doc.text('No transactions in the selected period.', margin, y);
  } else {
    data.forEach((row) => {
      if (y > pageH - 18) {
        doc.addPage();
        y = margin + 2;
        drawHeader();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.7);
      }
      const createdAt = row.created_at || row.createdAt || row.transaction_date || row.transactionDate;
      const dateText = formatDateValue(row.transaction_date || row.transactionDate || createdAt);
      const timeText = formatTimeValue(createdAt);
      const accountText = safeText(row.account_name || row.accountName);
      const amount = parseFloat(row.amount) || 0;
      const inText = isInflow(row.type) ? formatPKR(amount) : '—';
      const outText = isInflow(row.type) ? '—' : formatPKR(amount);
      const balanceText = formatPKR(parseFloat(row.balance_after ?? row.balanceAfter) || 0);
      const partyText = safeText(row.party_name || row.partyName || row.counterparty || row.reference_label || row.referenceLabel);
      const detailText = safeText(
        [row.description, row.reference_label && `Ref: ${row.reference_label}`].filter(Boolean).join(' | ')
      );
      const accountLines = doc.splitTextToSize(accountText, 33);
      const partyLines = doc.splitTextToSize(partyText, 24);
      const detailLines = doc.splitTextToSize(detailText, 21);
      const maxLines = Math.max(
        accountLines.length || 1,
        partyLines.length || 1,
        detailLines.length || 1
      );
      const rowHeight = Math.max(6.2, (maxLines * 3.8) + 1.8);

      doc.text(dateText, cols.date, y);
      doc.text(timeText, cols.time, y);
      doc.text(accountLines, cols.account, y);
      doc.text(inText, cols.in, y, { align: 'right' });
      doc.text(outText, cols.out, y, { align: 'right' });
      doc.text(balanceText, cols.balance, y, { align: 'right' });
      doc.text(partyLines, cols.counterparty, y);
      doc.text(detailLines, cols.desc, y);

      y += rowHeight;
    });
  }

  const stampY = pageH - 8;
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(`Printed on: ${new Date().toLocaleString('en-PK')}`, margin, stampY);
  doc.text(`Page ${doc.getCurrentPageInfo().pageNumber}`, pageW - margin, stampY, { align: 'right' });

  const safeBusiness = safeText(businessName).replace(/[^\w-]/g, '-').slice(0, 24);
  const safeFrom = safeText(fromDate).replace(/[^\d-]/g, '');
  const safeTo = safeText(toDate).replace(/[^\d-]/g, '');
  doc.save(`account-statement-${safeBusiness}-${safeFrom}-to-${safeTo}.pdf`);
}
